<template>
  <Teleport to="body">
    <Transition name="status-modal-overlay">
      <div
        v-if="show"
        class="warning-modal-overlay"
        :class="{ 'warning-modal-overlay--soft': isSoftTone }"
        :style="overlayStyle"
        role="presentation"
        @click.self="onOverlayClick"
      >
        <Transition name="status-modal-panel" appear>
          <div
            v-if="show"
            class="warning-modal"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="warning-modal-title"
            :aria-busy="loading ? 'true' : 'false'"
            @click.stop
          >
            <header class="warning-modal-header">
              <h3 id="warning-modal-title">{{ displayTitle }}</h3>
              <button
                type="button"
                class="warning-modal-close"
                aria-label="Close"
                :disabled="loading"
                @click="closeModal"
              >
                <i class="bi bi-x-lg" aria-hidden="true"></i>
              </button>
            </header>
            <div class="warning-modal-body">
              <div class="status-modal-body-content">
                <div class="status-modal-icon-wrap">
                  <span
                    class="status-modal-icon-ring"
                    :class="iconRingClass"
                    aria-hidden="true"
                  ></span>
                  <div
                    class="status-modal-icon"
                    :class="[iconClass, { 'warning-modal-icon--loading': loading }]"
                    aria-hidden="true"
                  >
                    <i :class="iconName"></i>
                  </div>
                </div>
                <div
                  v-if="displayMessageLines.length"
                  class="status-modal-message"
                  :class="messageClass"
                >
                  <p
                    v-for="(line, index) in displayMessageLines"
                    :key="'msg-' + index"
                    class="status-modal-message-line"
                    :style="{ animationDelay: `${0.14 + index * 0.07}s` }"
                  >{{ line }}</p>
                </div>
                <div v-if="loading" class="warning-modal-progress" aria-live="polite">
                  <div class="warning-modal-progress-track">
                    <div class="warning-modal-progress-bar"></div>
                  </div>
                  <small class="warning-modal-progress-hint">{{ loadingHint }}</small>
                </div>
              </div>
            </div>
            <footer class="warning-modal-footer">
              <template v-if="loading">
                <button
                  type="button"
                  class="status-modal-btn status-modal-btn--secondary"
                  disabled
                >{{ loadingButtonText }}</button>
              </template>
              <template v-else-if="confirmButtonText">
                <button
                  type="button"
                  class="status-modal-btn status-modal-btn--secondary"
                  @click="closeModal"
                >{{ buttonText }}</button>
                <button
                  v-if="altButtonText"
                  type="button"
                  class="status-modal-btn status-modal-btn--primary"
                  @click="altAction"
                >{{ altButtonText }}</button>
                <button
                  type="button"
                  class="status-modal-btn"
                  :class="isSoftTone ? 'status-modal-btn--primary-soft' : 'status-modal-btn--confirm'"
                  @click="confirmAction"
                >{{ confirmButtonText }}</button>
              </template>
              <button
                v-else
                type="button"
                class="status-modal-btn status-modal-btn--primary"
                @click="closeModal"
              >{{ buttonText }}</button>
            </footer>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
import './WarningModal.css';

export default {
  name: 'WarningModal',
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: 'Warning',
    },
    message: {
      type: String,
      default: '',
    },
    buttonText: {
      type: String,
      default: 'OK',
    },
    confirmButtonText: {
      type: String,
      default: '',
    },
    /** Optional third action (e.g. Open) between Cancel and Confirm. */
    altButtonText: {
      type: String,
      default: '',
    },
    /**
     * Visual tone: "warning" (amber/red) or "soft" (teal/slate choice dialog).
     */
    tone: {
      type: String,
      default: 'warning',
      validator: (v) => ['warning', 'soft'].includes(v),
    },
    /** When true, shows progress UI and blocks dismiss/confirm. */
    loading: {
      type: Boolean,
      default: false,
    },
    loadingTitle: {
      type: String,
      default: 'Please wait…',
    },
    loadingMessage: {
      type: String,
      default: 'Processing your request…',
    },
    loadingHint: {
      type: String,
      default: 'This may take a few seconds.',
    },
    loadingButtonText: {
      type: String,
      default: 'Working…',
    },
    /**
     * Optional overlay z-index. Use when this dialog must sit above another modal
     * (e.g. Invoicing progress at 10050).
     */
    overlayZIndex: {
      type: [Number, String],
      default: null,
    },
  },
  emits: ['close', 'confirm', 'alt'],
  computed: {
    isSoftTone() {
      return this.tone === 'soft';
    },
    overlayStyle() {
      if (this.overlayZIndex === null || this.overlayZIndex === '' || this.overlayZIndex === undefined) {
        return undefined;
      }
      return { zIndex: String(this.overlayZIndex) };
    },
    displayTitle() {
      return this.loading ? (this.loadingTitle || this.title) : this.title;
    },
    displayMessageLines() {
      const text = this.loading
        ? (this.loadingMessage || this.message)
        : this.message;
      return this.splitMessageLines(text);
    },
    iconRingClass() {
      return this.isSoftTone
        ? 'status-modal-icon-ring--soft'
        : 'status-modal-icon-ring--warning';
    },
    iconClass() {
      return this.isSoftTone
        ? 'status-modal-icon--soft'
        : 'status-modal-icon--warning';
    },
    iconName() {
      if (this.loading) return 'bi bi-arrow-repeat';
      return this.isSoftTone ? 'bi bi-file-earmark-pdf' : 'bi bi-exclamation-triangle-fill';
    },
    messageClass() {
      return this.isSoftTone
        ? 'status-modal-message--soft'
        : 'status-modal-message--warning';
    },
  },
  methods: {
    splitMessageLines(text) {
      if (!text || !String(text).trim()) return [];
      const lines = String(text)
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean);
      return lines.length ? lines : [String(text).trim()];
    },
    onOverlayClick() {
      if (this.loading) return;
      this.closeModal();
    },
    closeModal() {
      if (this.loading) return;
      this.$emit('close');
    },
    confirmAction() {
      if (this.loading) return;
      this.$emit('confirm');
    },
    altAction() {
      if (this.loading) return;
      this.$emit('alt');
    },
  },
};
</script>
